import React from 'react';
import PropTypes from 'prop-types';
import { LuArrowDown, LuArrowUp, LuArrowUpDown, LuChevronDown, LuMonitor, LuSmartphone } from 'react-icons/lu';
import useAccessText from '../accessText';
import { fullName, isActiveEmployee } from '../accessUtils';
import { PassiveTag, PersonAvatar } from './accessBits';

const SORTABLE = ['name', 'lastActivity', 'logins30', 'totalLogins', 'totalOnlineMs'];
const HEAD = 'tw-sticky tw-top-0 tw-z-[1] tw-h-10 tw-whitespace-nowrap tw-border-b tw-border-slate-200/80 tw-bg-slate-50 tw-px-3 tw-py-0 tw-text-left tw-align-middle tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-[0.08em] tw-text-slate-500 dark:tw-border-white/10 dark:tw-bg-[#282c35] dark:tw-text-slate-400';
const CELL = 'tw-whitespace-nowrap tw-border-b tw-border-slate-100 tw-px-3 tw-py-2.5 tw-align-middle tw-text-sm tw-text-slate-700 dark:tw-border-white/[0.06] dark:tw-text-slate-200';

/** Sortable per-user table; a row opens its 7-day / session figures and recent sign-ins. */
const UsageUsersTable = ({ rows, sortKey, sortDir, onSort, expandedId, onExpand }) => {
  const { label, formatDuration, formatRelative, formatDateTime, parseDevice } = useAccessText();

  const header = (key, columnKey) => {
    const active = sortKey === key;
    const Icon = !active ? LuArrowUpDown : sortDir === 'asc' ? LuArrowUp : LuArrowDown;
    return (
      <th className={HEAD} aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}>
        <button type="button" onClick={() => onSort(key)} className="tw-inline-flex tw-items-center tw-gap-1 tw-bg-transparent tw-uppercase tw-tracking-[0.08em] hover:tw-text-slate-800 dark:hover:tw-text-white">
          {label(`stats.columns.${columnKey}`)}
          <Icon aria-hidden="true" className={`tw-h-3 tw-w-3 ${active ? 'tw-text-teal-600 dark:tw-text-teal-300' : 'tw-opacity-40'}`} />
        </button>
      </th>
    );
  };

  return (
    <div className="tw-max-h-[60vh] tw-overflow-auto tw-rounded-2xl tw-border tw-border-slate-200/80 glass-scrollbar dark:tw-border-white/10">
      <table className="tw-w-full tw-border-separate tw-border-spacing-0">
        <thead>
          <tr>
            {header('name', 'user')}
            <th className={HEAD}>{label('stats.columns.status')}</th>
            {SORTABLE.slice(1).map((key) => <React.Fragment key={key}>{header(key, key)}</React.Fragment>)}
            <th className={HEAD} aria-label={label('stats.expand')} />
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const expanded = expandedId === row.emp.id;
            return (
              <React.Fragment key={row.emp.id}>
                <tr
                  onClick={() => onExpand(expanded ? null : row.emp.id)}
                  className={`tw-cursor-pointer tw-transition-colors ${expanded ? 'tw-bg-teal-50/70 dark:tw-bg-teal-500/[0.06]' : 'hover:tw-bg-slate-50 dark:hover:tw-bg-white/[0.03]'}`}
                >
                  <td className={CELL}>
                    <span className="tw-flex tw-min-w-[12rem] tw-items-center tw-gap-2.5">
                      <PersonAvatar employee={row.emp} passive={!isActiveEmployee(row.emp)} size="avatar" />
                      <span className="tw-min-w-0">
                        <span className="tw-flex tw-items-center tw-gap-1.5">
                          <span className="tw-truncate tw-font-semibold tw-text-slate-900 dark:tw-text-white">{fullName(row.emp)}</span>
                          {!isActiveEmployee(row.emp) && <PassiveTag />}
                        </span>
                        <span className="tw-block tw-truncate tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{row.emp.eMail || '—'}</span>
                      </span>
                    </span>
                  </td>
                  <td className={CELL}>
                    {row.isOnline ? (
                      <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-emerald-500/10 tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-semibold tw-text-emerald-700 dark:tw-text-emerald-300">
                        <span aria-hidden="true" className="tw-h-1.5 tw-w-1.5 tw-rounded-full tw-bg-emerald-500" />
                        {label('stats.statusOnline')}
                        {row.currentSessionMs ? <span className="tw-font-medium tw-opacity-80">({formatDuration(row.currentSessionMs)})</span> : null}
                      </span>
                    ) : (
                      <span className="tw-text-xs tw-font-medium tw-text-slate-400 dark:tw-text-slate-500">{label('stats.statusOffline')}</span>
                    )}
                  </td>
                  <td className={CELL} title={formatDateTime(row.lastActivity)}>{formatRelative(row.lastActivity)}</td>
                  <td className={`${CELL} tw-text-right tw-font-semibold tw-tabular-nums`}>{row.logins30}</td>
                  <td className={`${CELL} tw-text-right tw-tabular-nums`}>{row.totalLogins}</td>
                  <td className={`${CELL} tw-text-right tw-tabular-nums`}>{formatDuration(row.totalOnlineMs)}</td>
                  <td className={`${CELL} tw-w-10 tw-text-slate-400`}>
                    <LuChevronDown aria-label={expanded ? label('stats.collapse') : label('stats.expand')} className={`tw-h-4 tw-w-4 tw-transition-transform ${expanded ? 'tw-rotate-180' : ''}`} />
                  </td>
                </tr>
                {expanded && (
                  <tr>
                    <td colSpan={7} className="tw-border-b tw-border-slate-100 tw-bg-slate-50/70 tw-px-4 tw-py-4 dark:tw-border-white/[0.06] dark:tw-bg-white/[0.02]">
                      <div className="tw-grid tw-grid-cols-3 tw-gap-2 sm:tw-max-w-xl">
                        {[
                          [label('stats.logins7'), row.logins7],
                          [label('stats.avgSession'), formatDuration(row.avgSessionMs)],
                          [row.isOnline ? label('stats.activeSession') : label('stats.lastSession'), formatDuration(row.isOnline ? row.currentSessionMs : row.lastSessionMs)],
                        ].map(([name, value]) => (
                          <div key={name} className="tw-rounded-xl tw-border tw-border-slate-200/80 tw-bg-white/80 tw-px-3 tw-py-2 dark:tw-border-white/10 dark:tw-bg-white/[0.04]">
                            <p className="tw-text-[11px] tw-font-medium tw-text-slate-500 dark:tw-text-slate-400">{name}</p>
                            <p className="tw-text-base tw-font-semibold tw-tabular-nums tw-text-slate-900 dark:tw-text-white">{value}</p>
                          </div>
                        ))}
                      </div>
                      <p className="tw-mt-4 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-[0.1em] tw-text-slate-400">{label('stats.recentTitle', { count: row.logins30 })}</p>
                      {row.recent.length === 0 ? (
                        <p className="tw-mt-2 tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">{label('stats.recentEmpty')}</p>
                      ) : (
                        <ul className="tw-mt-2 tw-grid tw-gap-1.5 sm:tw-grid-cols-2">
                          {row.recent.slice(0, 10).map((entry, index) => {
                            const device = entry.synthetic ? { label: label('stats.sessionRecord'), isMobile: false } : parseDevice(entry.device);
                            const DeviceIcon = device.isMobile ? LuSmartphone : LuMonitor;
                            return (
                              <li key={`${entry.ts}-${index}`} className="tw-flex tw-items-center tw-gap-2 tw-rounded-xl tw-bg-white/80 tw-px-3 tw-py-1.5 tw-text-xs dark:tw-bg-white/[0.04]">
                                <DeviceIcon aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-shrink-0 tw-text-slate-400" />
                                <span className="tw-font-medium tw-tabular-nums tw-text-slate-700 dark:tw-text-slate-200">{formatDateTime(entry.ts)}</span>
                                <span className="tw-ml-auto tw-truncate tw-text-slate-500 dark:tw-text-slate-400">{device.label}</span>
                              </li>
                            );
                          })}
                          {row.recent.length > 10 && (
                            <li className="tw-px-3 tw-py-1.5 tw-text-xs tw-text-slate-500">{label('stats.more', { count: row.recent.length - 10 })}</li>
                          )}
                        </ul>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

UsageUsersTable.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  sortKey: PropTypes.string.isRequired,
  sortDir: PropTypes.string.isRequired,
  onSort: PropTypes.func.isRequired,
  expandedId: PropTypes.string,
  onExpand: PropTypes.func.isRequired,
};

export default UsageUsersTable;
